/**
 * Bank details + Confirmation of Payee (GoCardless) check. Self-contained and
 * application-agnostic — used by Broker Onboarding and reusable elsewhere.
 * Calls POST /contracts/bank-details/verify and reports the verified details
 * back via onVerified(bankDetails).
 */
import { useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, AlertCircle, XCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

export default function BankCoPCheck({ defaultAccountName = "", onVerified }) {
  const [sortCode, setSortCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState(defaultAccountName);
  const [status, setStatus] = useState(null); // verifying|verified|partial|no_match|error
  const [result, setResult] = useState(null);

  const cleanSort = sortCode.replace(/-/g, "");
  const canVerify = cleanSort.length === 6 && accountNumber.length === 8;

  const runCheck = async () => {
    if (!canVerify) return;
    try {
      setStatus("verifying");
      const { data } = await api.post("/contracts/bank-details/verify", {
        account_number: accountNumber,
        sort_code: sortCode,
        account_name: accountName || "",
      });
      setResult(data);
      const verified = {
        account_number: accountNumber,
        sort_code: sortCode,
        account_name: accountName,
        bank_name: data.bank_name,
        match_status: data.match_status,
        provider: data.provider,
        verified: data.cop_verified || data.match_status === "partial_match",
        verified_at: new Date().toISOString(),
      };
      if (data.cop_verified) {
        setStatus("verified");
        toast.success("Bank details verified via Confirmation of Payee");
        onVerified?.(verified);
      } else if (data.match_status === "partial_match") {
        setStatus("partial");
        toast.warning("Account valid — name match unavailable (flagged for review)");
        onVerified?.(verified);
      } else if (data.match_status === "no_match") {
        setStatus("no_match");
        toast.error("No match — bank details not recognised");
      } else {
        setStatus("error");
        toast.error("Bank verification failed");
      }
    } catch (e) {
      console.error("Bank CoP check error:", e);
      setStatus("error");
      toast.error(errorDetailMessage(e, "Failed to verify bank details"));
    }
  };

  const reset = () => { setStatus(null); setResult(null); };

  return (
    <div className="space-y-4" data-testid="bank-cop-check">
      {status === "verified" && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-800">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">CoP Verification Successful</span>
          </div>
        </div>
      )}
      {status === "partial" && result && (
        <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg">
          <div className="flex items-start gap-2 text-amber-800">
            <AlertCircle className="w-5 h-5 mt-0.5" />
            <div>
              <span className="font-medium">Account Valid — Name Unverified</span>
              <p className="text-sm text-amber-700 mt-1">
                {result.bank_name ? `Account confirmed at ${result.bank_name}. ` : ""}
                Name matching isn't available yet, so this is flagged for review. You can still proceed.
              </p>
            </div>
          </div>
        </div>
      )}
      {status === "no_match" && (
        <div className="p-4 bg-red-50 border border-red-300 rounded-lg">
          <div className="flex items-start gap-2 text-red-800">
            <XCircle className="w-5 h-5 mt-0.5" />
            <div>
              <span className="font-medium">No Match Found</span>
              <p className="text-sm text-red-700 mt-1">
                Bank details not recognised. Please check the sort code and account number.
              </p>
            </div>
          </div>
        </div>
      )}
      {status === "error" && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-800">
            <XCircle className="w-5 h-5" />
            <span className="font-medium">Verification failed — please try again</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="broker-sort-code">Sort Code *</Label>
          <Input
            id="broker-sort-code"
            placeholder="00-00-00"
            value={sortCode}
            data-testid="broker-sort-code"
            onChange={(e) => {
              let v = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
              if (v.length >= 4) v = v.slice(0, 2) + "-" + v.slice(2, 4) + "-" + v.slice(4);
              else if (v.length >= 2) v = v.slice(0, 2) + "-" + v.slice(2);
              setSortCode(v); reset();
            }}
            disabled={status === "verifying"}
          />
        </div>
        <div>
          <Label htmlFor="broker-account-number">Account Number *</Label>
          <Input
            id="broker-account-number"
            placeholder="12345678"
            maxLength={8}
            value={accountNumber}
            data-testid="broker-account-number"
            onChange={(e) => { setAccountNumber(e.target.value.replace(/[^0-9]/g, "").slice(0, 8)); reset(); }}
            disabled={status === "verifying"}
          />
        </div>
      </div>
      <div>
        <Label htmlFor="broker-account-name">Account Name *</Label>
        <Input
          id="broker-account-name"
          placeholder="Must match name on bank account"
          value={accountName}
          data-testid="broker-account-name"
          onChange={(e) => { setAccountName(e.target.value); reset(); }}
          disabled={status === "verifying"}
        />
      </div>

      {status !== "verified" && (
        <Button
          onClick={runCheck}
          disabled={!canVerify || status === "verifying"}
          className="w-full"
          data-testid="broker-run-cop-btn"
        >
          {status === "verifying" ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying…</>
          ) : (
            <><ShieldCheck className="w-4 h-4 mr-2" />Run Confirmation of Payee</>
          )}
        </Button>
      )}
    </div>
  );
}
